import { CalendarToday, AttachMoney } from '@mui/icons-material';
import { Grid, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import { SelectedPatient, errorsState } from 'atoms';
import FormHeading from 'common/FormHeading';
import FormSearhcAndSelectInput from 'common/FormSearchAndSelectInput';
import FormSelectInput from 'common/FormSelectInput';
import FormTextInput from 'common/FormTextInput';
import FormInputLabel from 'common/FormInputLabel';
import { genderOptions, serviceTimeOptions } from 'common/constant';
import { useRecoilState, useRecoilValue } from 'recoil';
import { theme } from 'theme';
import moment from 'moment';
import { useAuth } from 'context/AuthContext';

import { useEffect, useRef, useState } from 'react';
import { getProviderAppointments } from 'utils/methods';

export default function Form({
    formData,
    setFormData,
    handleChange,
    servicesCount,
    handleServiceChange,
    services,
    business,
    handleRemoveService,
    providers = [],
    setProviders,
    locations,
    providerAvailabilityData,
    oooData,
    cachedProviderAppointments,
    handleAddAnotherService,
    onEmailValidChange,
}) {
    const { token } = useAuth();
    const [patients, setPatients] = useState([]);
    const [addNewPatient, setAddNewPatient] = useState(false);
    const [patientDetails, setPatientDetails] = useState({
        email: '',
    });
    // New state for email typeahead and conditional display of additional fields
    const [emailQuery, setEmailQuery] = useState('');
    const [emailMatches, setEmailMatches] = useState([]);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [showEmailDropdown, setShowEmailDropdown] = useState(false);
    const [emailDropdownPosition, setEmailDropdownPosition] = useState('below');
    const emailDropdownRef = useRef(null);
    const [showNewPatientDetails, setShowNewPatientDetails] = useState(false);
    const [emailError, setEmailError] = useState('');
    // Search Patient mode states
    const [searchPatientMode, setSearchPatientMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [searchDropdownPosition, setSearchDropdownPosition] = useState('below');
    const searchDropdownRef = useRef(null);
    const [patientAutocompleteValue, setPatientAutocompleteValue] = useState('');
    const [patientRemoteMatches, setPatientRemoteMatches] = useState([]);

    const [isTimeDisabled, setIsTimeDisabled] = useState(false);
    const errors = useRecoilValue(errorsState);
    const [selectedPatient, setSelectedPatient] = useRecoilState(SelectedPatient);
    const [bookedSlots, setBookedSlots] = useState([]);
    const isBusinessSelected = typeof formData?.provider === 'string' && String(formData?.provider).startsWith('business_');

    // Normalize text to Title Case for read-only displays in edit/view
    const toTitleCase = (value) => {
        if (!value || typeof value !== 'string') return value;
        return value
            .toLowerCase()
            .split(' ')
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    };

    // Helper function to check if appointment is cancelled
    const isAppointmentCancelled = () => {
        return formData?.appointmentStatus?.toLowerCase()?.includes('cancelled');
    };

    // Helper function to check if appointment is past
    const isPastAppointment = () => {
        // Check form_status (past appointments are set to 'view')
        if (formData?.form_status === 'view') {
            return true;
        }

        // Check isPastAppointment flag
        if (formData?.isPastAppointment) {
            return true;
        }

        // Check appointment status
        const statusIsPast = String(formData?.appointmentStatus || '').toLowerCase().includes('past');
        if (statusIsPast) {
            return true;
        }

        // Check if the appointment date/time is actually in the past
        if (formData?.dateOfService) {
            const dateOnly = moment(formData.dateOfService);
            if (dateOnly.isValid()) {
                if (!formData?.timeOfService) {
                    // If no time, check if date is before today
                    if (dateOnly.isBefore(moment(), 'day')) {
                        return true;
                    }
                } else {
                    // Parse date and time together
                    const base = dateOnly.format('YYYY-MM-DD');
                    const dt = moment(`${base} ${formData.timeOfService}`, [
                        'YYYY-MM-DD HH:mm:ss',
                        'YYYY-MM-DD HH:mm',
                        'YYYY-MM-DD h:mm A',
                        'YYYY-MM-DD hh:mm A',
                        moment.ISO_8601,
                    ]);
                    // Compare at minute granularity so a slot at the current
                    // minute (e.g. selecting 12:00 when it's 12:00:30) isn't
                    // treated as past, which keeps this consistent with the
                    // time picker's filtering and avoids freezing the form.
                    if (dt.isValid() && dt.isBefore(moment(), 'minute')) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    useEffect(() => {
        if (formData?.form_status === 'edit' || formData?.form_status === 'view') {
            const checkIfTimeShouldBeDisabled = () => {
                // If appointment is cancelled, all fields should be disabled
                if (isAppointmentCancelled()) {
                    setIsTimeDisabled(true);
                    return;
                }

                // Disable date/time for past appointments only
                // Any future appointment (regardless of confirmed/pending status) can be edited
                if (isPastAppointment()) {
                    setIsTimeDisabled(true);
                    return;
                }

                // Allow editing for any future appointment
                setIsTimeDisabled(false);
            };

            checkIfTimeShouldBeDisabled();
            const interval = setInterval(checkIfTimeShouldBeDisabled, 1000 * 60);

            return () => clearInterval(interval);
        }
    }, [formData?.form_status, formData?.appointmentStatus, formData?.dateOfService, formData?.timeOfService, formData?.isPastAppointment]);

    // When the date changes to today, clear any previously selected time that is
    // now in the past (e.g. user picked a future date + early time, then switched
    // back to today). Leaving the stale past time would mark the appointment as
    // "past" and disable the whole form.
    useEffect(() => {
        if (formData?.form_status === 'view') return; // past/read-only appointments
        if (isAppointmentCancelled()) return;
        if (!formData?.dateOfService || !formData?.timeOfService) return;

        const selectedDate = moment(formData.dateOfService);
        if (!selectedDate.isValid()) return;
        if (!selectedDate.isSame(moment(), 'day')) return; // only restrict for today

        const dt = moment(`${selectedDate.format('YYYY-MM-DD')} ${formData.timeOfService}`, [
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD HH:mm',
            'YYYY-MM-DD h:mm A',
            'YYYY-MM-DD hh:mm A',
        ]);

        if (dt.isValid() && dt.isBefore(moment(), 'minute')) {
            setFormData((prev) => ({ ...prev, timeOfService: '' }));
        }
    }, [formData?.dateOfService]);

    useEffect(() => {
        const filterDateBookedSlots = async () => {
            if (!formData?.provider || isBusinessSelected || !formData?.dateOfService || !business?.id) return;

            try {
                let appointmentsList;

                if (Array.isArray(cachedProviderAppointments)) {
                    // Use cached appointments from provider-full-details, filter to active statuses
                    appointmentsList = cachedProviderAppointments.filter(
                        (app) => app.status === 'booked' || app.status === 'pending'
                    );
                } else {
                    appointmentsList = await getProviderAppointments(Number(formData?.provider), business.id, token);
                }

                const bookedTimes = appointmentsList
                    .filter((app) => app.appointment_date === formData?.dateOfService)
                    .map((app) => app.time);

                setBookedSlots(bookedTimes);
            } catch (error) {
                console.error("Failed to fetch booked slots", error);
            }
        };

        filterDateBookedSlots();
    }, [formData?.provider, formData?.dateOfService, isBusinessSelected, business?.id, cachedProviderAppointments]);

    const durationOPtions = [
        { id: 15, name: '15 Minutes' },
        { id: 30, name: '30 Minutes' },
        { id: 45, name: '45 Minutes' },
        { id: 60, name: '1 Hour' },
        { id: 75, name: '1 Hour 15 Minutes' },
        { id: 90, name: '1 Hour 30 Minutes' },
        { id: 105, name: '1 Hour 45 Minutes' },
        { id: 120, name: '2 Hours' },
    ];

    const getPatients = async () => {
        if (!business?.id) {
            console.warn('Business ID not available. Cannot fetch patients.');
            return;
        }

        try {
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setPatients([]);
                return;
            }

            // REQUIREMENT: Show patients who have appointments OR purchased services with this business
            
            // Fetch appointments from backend API
            const appointmentsResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            let appointmentPatientIds = [];
            if (appointmentsResponse.ok) {
                const appointmentsResult = await appointmentsResponse.json();
                if (appointmentsResult.success && appointmentsResult.data) {
                    const appointmentsData = Array.isArray(appointmentsResult.data) ? appointmentsResult.data : [];
                    // Extract unique patient IDs from appointments
                    appointmentPatientIds = appointmentsData
                        .filter(app => app.patient_id !== null && app.patient_id !== undefined)
                        .map(app => app.patient_id);
                }
            }
            
            // Fetch purchased services from backend API
            const purchasedServicesResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/purchased_services`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            let purchasedServicePatientIds = [];
            if (purchasedServicesResponse.ok) {
                const purchasedServicesResult = await purchasedServicesResponse.json();
                if (purchasedServicesResult.success && purchasedServicesResult.data) {
                    const purchasedServicesData = Array.isArray(purchasedServicesResult.data) ? purchasedServicesResult.data : [];
                    // Extract unique patient IDs from purchased services
                    purchasedServicePatientIds = purchasedServicesData
                        .filter(ps => ps.patient_id !== null && ps.patient_id !== undefined)
                        .map(ps => ps.patient_id);
                }
            }
            
            // Combine and extract unique patient IDs from both sources
            const allPatientIds = [...appointmentPatientIds, ...purchasedServicePatientIds];
            const uniquePatientIds = [...new Set(allPatientIds)];
            
            if (uniquePatientIds.length === 0) {
                // No patients with appointments or purchased services found
                setPatients([]);
                return;
            }
            
            // Fetch the actual patient records for these IDs using backend API
            const patientsResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ ids: uniquePatientIds })
                }
            );

            if (patientsResponse.ok) {
                const patientsResult = await patientsResponse.json();
                if (patientsResult.success && patientsResult.data !== undefined) {
                    const patientsData = Array.isArray(patientsResult.data) ? patientsResult.data : [];
                    setPatients(patientsData);
                } else {
                    setPatients([]);
                }
            } else {
                console.error('Error fetching patients from backend API');
                setPatients([]);
            }
            
        } catch (error) {
            console.error('Unexpected error in getPatients:', error);
            setPatients([]);
        }
    };
    
    //Edit Patient case
    const getPatientDetail = () => {
        const selectedPatient = patients.find((patient) => patient.id === parseInt(formData?.patient));
        setPatientDetails({
            name: selectedPatient?.first_name + selectedPatient?.last_name,
            email: selectedPatient?.email,
            phone: selectedPatient?.contact_number,
            dob: selectedPatient?.dob,
            gender: selectedPatient?.gender,
        });
    };
    const handelPatientChange = (e) => {
        const { value } = e.target;
        if (value === 'addNewPatient') {
            setSearchPatientMode(false);
            setAddNewPatient(true);
            setShowNewPatientDetails(true); // Show all patient detail inputs immediately
            setPatientDetails({});
            const { patient, ...rest } = formData;
            setFormData({
                ...rest,
                name: '',
                email: '',
                phone: '',
                dob: '',
                gender: '',
            });
            // Reset email typeahead state when switching to add new patient
            setEmailQuery('');
            setEmailMatches([]);
            setShowEmailDropdown(false);
            // Keep showNewPatientDetails true - don't hide the fields
        } else if (value === 'searchPatient') {
            // Enter search mode
            setAddNewPatient(false);
            setSearchPatientMode(true);
            setPatientDetails({});
            const { patient, name, email, phone, dob, gender, ...rest } = formData || {};
            setFormData({
                ...rest,
            });
            setSearchQuery('');
            setSearchResults([]);
            setShowSearchDropdown(false);
        } else if (value && value !== '') {
            // Only process if a valid patient is selected
            setSearchPatientMode(false);
            const selectedPatient =
                (patients || []).find((patient) => Number(patient.id) === Number(value)) ||
                (patientRemoteMatches || []).find((patient) => Number(patient.id) === Number(value));
            if (selectedPatient) {
                setPatientDetails({
                    email: selectedPatient?.email,
                    phone: selectedPatient?.contact_number,
                    dob: selectedPatient?.dob,
                    gender: selectedPatient?.gender,
                });
                setSelectedPatient({
                    id: selectedPatient.id,
                    first_name: selectedPatient?.first_name,
                    email: selectedPatient?.email,
                    phone: selectedPatient?.contact_number,
                    dob: selectedPatient?.dob,
                    gender: selectedPatient?.gender,
                });
                // Ensure remotely searched patients are available in local options after selection
                const existsInLocal = (patients || []).some((patient) => Number(patient.id) === Number(selectedPatient.id));
                if (!existsInLocal) {
                    setPatients([...(patients || []), selectedPatient]);
                }
            }
            handleChange(e);
        } else {
            // Handle empty/placeholder selection
            setPatientDetails({});
            setSelectedPatient({});
            setAddNewPatient(false);
            setSearchPatientMode(false);
            handleChange(e);
        }
    };

 

    useEffect(() => {
        getPatients().then(() => { 
        });
        // getProviders().then(() => {});
    }, []); // Removed formData dependency to prevent infinite loop

    useEffect(() => {
        getPatientDetail();
    }, [patients, formData?.patient]); // Only depend on the specific patient ID, not all formData

    const getMinTime = (selectedDate) => {
        const currentDate = new Date();
        const hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();

        const currentTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        if (selectedDate) {
            const selectedDateObj = new Date(selectedDate);
            if (selectedDateObj.toDateString() === currentDate.toDateString()) {
                return currentTime;
            }
        }
        return null;
    };

    const formatPhoneNumber = (phone) => {
        try {
            if (!phone) return "";

            // Remove all non-digit characters
            let cleaned = phone.replace(/\D/g, "");

            // Ensure it starts with '1' for US numbers
            if (!cleaned.startsWith("1")) {
                cleaned = "1" + cleaned;
            }

            // Format as +1 (XXX) XXX-XXXX
            if (cleaned.length <= 4) return `+1 (${cleaned.slice(1)}`;
            if (cleaned.length <= 7) return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4)}`;
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
        } catch (error) {
 
            return phone;
        }
    };

    const handlePhoneChange = (e) => {
        let { name, value } = e.target;

        // Format phone number for UI
        value = formatPhoneNumber(value);

        handleChange({ target: { name, value } });
    };

    const isCreatedByPatient = formData?.form_status === 'edit' && (!formData?.duration || formData?.servicesData?.length <= 0)

    const getServicePrice = () => {
        // const appointmentType = formData?.appointment_type
        // if (appointmentType === "in_office") {
        //     return formData?.providerDetail?.in_person_consultation_price
        // }
        // else if (appointmentType === 'telemedicine') {
        //     return formData?.providerDetail?.telemedicine_consultation_price
        // }
        // else return 0

        return 0
    }

    // Helper function to format price with cents
    const formatPriceWithCents = (price) => {
        if (price === undefined || price === null || price === '') return '';
        const priceNum = parseFloat(price);
        if (isNaN(priceNum)) return price;
        return priceNum.toFixed(2);
    }
    const handlePriceChange = (e, idx) => {
        const { value } = e.target;
        
        // Remove all non-numeric characters
        const numericValue = value.replace(/[^0-9]/g, '');
        
        // Format as currency: convert to cents, then format back
        let finalValue = '';
        if (numericValue) {
            // Convert to number and divide by 100 to get dollars
            const cents = parseInt(numericValue, 10);
            const dollars = cents / 100;
            finalValue = dollars.toFixed(2);
        }
        
        handleServiceChange({ target: { name: 'price', value: finalValue } }, idx);
    }

    const handlePriceFocus = (e, idx) => {
        // If the value is empty, set it to "0.00" and select it for easy editing
        const currentValue = formData?.servicesData?.[idx]?.price;
        if (!currentValue || currentValue === '') {
            const updatedServices = [...(formData?.servicesData || [])];
            if (!updatedServices[idx]) {
                updatedServices[idx] = {};
            }
            updatedServices[idx].price = '0.00';
            setFormData({ ...formData, servicesData: updatedServices });
            setTimeout(() => {
                e.target.select();
            }, 0);
        } else if (currentValue === '0.00') {
            setTimeout(() => {
                e.target.select();
            }, 0);
        }
    }

    // Email typeahead: search existing patients by email
    useEffect(() => {
        const run = async () => {
            try {
                if (!addNewPatient) return;
                const query = (emailQuery || '').trim();
                if (query.length < 2) {
                    setEmailMatches([]);
                    setShowEmailDropdown(false);
                    return;
                }
                setIsEmailLoading(true);
                
                // Validate access token
                if (!token) {
                    console.error('No authentication token found');
                    setEmailMatches([]);
                    setShowEmailDropdown(false);
                    setIsEmailLoading(false);
                    return;
                }

                // Search patients by email using backend API
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ search: query.trim(), limit: 10 })
                    }
                );

                if (!response.ok) {
                    setEmailMatches([]);
                    setShowEmailDropdown(false);
                    setIsEmailLoading(false);
                    return;
                }

                const result = await response.json();
                const data = Array.isArray(result.data) ? result.data : [];
                
                // Filter results to match email search (backend search might search by name/email/phone)
                const emailFiltered = data.filter(patient => 
                    patient.email && patient.email.toLowerCase().includes(query.toLowerCase())
                );
                
                setEmailMatches(emailFiltered || []);
                setShowEmailDropdown(!!(emailFiltered && emailFiltered.length > 0));
                // If no matches and user has typed a reasonably long email, show fields for manual entry
                if ((!data || data.length === 0) && query.length >= 3) {
                    setShowNewPatientDetails(true);
                }
            } catch (_) {
                setEmailMatches([]);
                setShowEmailDropdown(false);
            } finally {
                setIsEmailLoading(false);
            }
        };
        const t = setTimeout(run, 300);
        return () => clearTimeout(t);
    }, [emailQuery, addNewPatient]);

    // Position dropdown above/below based on space
    useEffect(() => {
        if (showEmailDropdown && emailDropdownRef.current) {
            const rect = emailDropdownRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;
            setEmailDropdownPosition(spaceBelow < 200 && spaceAbove > 200 ? 'above' : 'below');
        }
    }, [showEmailDropdown, emailMatches]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emailDropdownRef.current && !emailDropdownRef.current.contains(event.target)) {
                setShowEmailDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search bar: query patients by name/email/phone
    useEffect(() => {
        const run = async () => {
            try {
                if (!searchPatientMode) return;
                const query = (searchQuery || '').trim();
                if (query.length < 2) {
                    setSearchResults([]);
                    setShowSearchDropdown(false);
                    return;
                }
                setIsSearchLoading(true);
                
                // Validate access token
                if (!token) {
                    console.error('No authentication token found');
                    setSearchResults([]);
                    setShowSearchDropdown(false);
                    setIsSearchLoading(false);
                    return;
                }

                // Search patients using backend API (searches by first_name, last_name, and email)
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ search: query.trim(), limit: 15 })
                    }
                );

                let combined = [];
                if (response.ok) {
                    const result = await response.json();
                    combined = Array.isArray(result.data) ? result.data : [];
                }

                // Optional phone exact match when user typed digits
                const digits = query.replace(/\D/g, '');
                if (digits.length >= 4) {
                    const phoneResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/patients?contact_number=${digits}&limit=15`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );

                    if (phoneResponse.ok) {
                        const phoneResult = await phoneResponse.json();
                        // Backend may return { success, data } or the data directly
                        const dataPhone = phoneResult?.success 
                            ? (Array.isArray(phoneResult.data) ? phoneResult.data : [phoneResult.data])
                            : (Array.isArray(phoneResult) ? phoneResult : [phoneResult]);
                        
                        if (Array.isArray(dataPhone) && dataPhone.length > 0) {
                            const seen = new Set(combined.map(p => p.id));
                            for (const p of dataPhone) {
                                if (!seen.has(p.id)) {
                                    combined.push(p);
                                    seen.add(p.id);
                                }
                            }
                        }
                    }
                }

                setSearchResults(combined);
                setShowSearchDropdown(combined.length > 0);
            } catch (_) {
                setSearchResults([]);
                setShowSearchDropdown(false);
            } finally {
                setIsSearchLoading(false);
            }
        };
        const t = setTimeout(run, 300);
        return () => clearTimeout(t);
    }, [searchQuery, searchPatientMode]);

    // Position search dropdown above/below based on space
    useEffect(() => {
        if (showSearchDropdown && searchDropdownRef.current) {
            const rect = searchDropdownRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;
            setSearchDropdownPosition(spaceBelow < 200 && spaceAbove > 200 ? 'above' : 'below');
        }
    }, [showSearchDropdown, searchResults]);

    // Close search dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
                setShowSearchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleEmailInputChange = (e) => {
        const { value } = e.target;
        setEmailQuery(value);
        setEmailError('');
        onEmailValidChange?.(true);
        // Always set the email in form data
        handleChange({ target: { name: 'email', value } });
        
        // Keep patient detail fields visible when adding new patient
        // Only hide fields if email is cleared AND we're not in addNewPatient mode
        if (!value.trim() && !formData?.patient && !addNewPatient) {
            // If email is cleared and no patient was selected and not adding new patient, hide all patient detail fields
            setShowNewPatientDetails(false);
        } else if (addNewPatient) {
            // When adding new patient, always show all fields
            setShowNewPatientDetails(true);
        }
        
        // Clear any previously selected patient link and reset patient fields when typing
        if (formData?.patient) {
            const { patient, name, phone, dob, gender, ...rest } = formData;
            setFormData({
                ...rest,
                email: value,
                name: '',
                phone: '',
                dob: '',
                gender: '',
            });
            // Also clear selected patient linkage
            setSelectedPatient({});
        } else if (addNewPatient) {
            // In manual entry mode (no selected patient), reset entered fields on any email change
            setFormData({
                ...formData,
                email: value,
                name: '',
                phone: '',
                dob: '',
                gender: '',
            });
        }
        
        // Reset selected patient state only if no patient was selected
        if (!formData?.patient) {
            setSelectedPatient({});
        }
    };

    const handleEmailBlur = () => {
        if (showEmailDropdown) return;
        const val = (formData?.email || '').trim();
        if (addNewPatient && val.length > 0) {
            setTimeout(() => setShowNewPatientDetails(true), 150);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(val)) {
                setEmailError('Please enter a valid email address');
                onEmailValidChange?.(false);
            }
        }
    };

    const handleEmailPatientSelect = (p) => {
        const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim();
        setFormData({
            ...formData,
            patient: p.id,
            name: fullName,
            email: p.email || formData.email,
            phone: p.contact_number || '',
            dob: p.dob || '',
            gender: p.gender || '',
        });
        setSelectedPatient({
            id: p.id,
            first_name: p.first_name,
            email: p.email,
            phone: p.contact_number,
            dob: p.dob,
            gender: p.gender,
        });
        setEmailQuery(p.email || '');
        setEmailError('');
        onEmailValidChange?.(true);
        setShowEmailDropdown(false);
        setShowNewPatientDetails(true);
    };
    const handleSearchPatientSelect = (p) => {
        setFormData({
            ...formData,
            patient: p.id,
        });
        setPatientDetails({
            email: p.email || '',
            phone: p.contact_number || '',
            dob: p.dob || '',
            gender: p.gender || '',
        });
        setSelectedPatient({
            id: p.id,
            first_name: p.first_name,
            email: p.email,
            phone: p.contact_number,
            dob: p.dob,
            gender: p.gender,
        });
        // Ensure the selected patient appears in the dropdown options
        try {
            const exists = patients?.some((pt) => Number(pt.id) === Number(p.id));
            if (!exists) {
                setPatients([...(patients || []), p]);
            }
        } catch (_) {}
        setSearchQuery('');
        setShowSearchDropdown(false);
        setSearchPatientMode(false);
    };
    const getPatientOptionLabel = (patient) => {
        const fullName = `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim();
        return fullName || patient?.email || '';
    };
    const sortedPatients = [...(patients || [])].sort((a, b) => {
        const nameA = getPatientOptionLabel(a).toLowerCase();
        const nameB = getPatientOptionLabel(b).toLowerCase();
        return nameA.localeCompare(nameB);
    });
    const mergedPatientOptions = [...sortedPatients];
    (patientRemoteMatches || []).forEach((remotePatient) => {
        const exists = mergedPatientOptions.some((patient) => Number(patient?.id) === Number(remotePatient?.id));
        if (!exists) {
            mergedPatientOptions.push(remotePatient);
        }
    });
    const patientOptionEntries = [];
    const usedPatientLabels = new Set();
    mergedPatientOptions.forEach((patient) => {
        const baseLabel = getPatientOptionLabel(patient);
        if (!baseLabel) return;
        const emailPart = (patient?.email || '').trim();
        let finalLabel = baseLabel.trim();
        if (usedPatientLabels.has(finalLabel.toLowerCase()) && emailPart) {
            finalLabel = `${baseLabel} (${emailPart})`;
        }
        usedPatientLabels.add(finalLabel.toLowerCase());
        patientOptionEntries.push({ label: finalLabel, patient });
    });
    const patientDropdownOptions = [
        '+ Add new patient',
        ...patientOptionEntries.map((entry) => entry.label),
    ];
    const handlePatientAutocompleteChange = (_e, newValue) => {
        const selectedValue = String(newValue ?? '').trim();
        setPatientAutocompleteValue(selectedValue);

        if (selectedValue === '+ Add new patient') {
            handelPatientChange({ target: { name: 'patient', value: 'addNewPatient' } });
            return;
        }

        const selectedPatientEntry = patientOptionEntries.find(
            (entry) => entry.label.toLowerCase() === selectedValue.toLowerCase(),
        );
        const selectedPatientOption = selectedPatientEntry?.patient;

        if (selectedPatientOption) {
            handelPatientChange({ target: { name: 'patient', value: String(selectedPatientOption.id) } });
            return;
        }

        if (!selectedValue) {
            handelPatientChange({ target: { name: 'patient', value: '' } });
        }
    };

    useEffect(() => {
        const runPatientSearch = async () => {
            try {
                const query = (patientAutocompleteValue || '').trim();
                if (query.length < 2) {
                    setPatientRemoteMatches([]);
                    return;
                }

                const normalizedQuery = query.toLowerCase();
                const isSpecialOption = normalizedQuery === '+ add new patient';
                const hasLocalExactMatch = (patients || []).some(
                    (patient) => getPatientOptionLabel(patient).toLowerCase() === normalizedQuery,
                );
                const hasRemoteExactMatch = (patientRemoteMatches || []).some(
                    (patient) => getPatientOptionLabel(patient).toLowerCase() === normalizedQuery,
                );

                if (isSpecialOption || hasLocalExactMatch || hasRemoteExactMatch) {
                    return;
                }

                if (!token) {
                    setPatientRemoteMatches([]);
                    return;
                }

                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ search: query, limit: 15 })
                    },
                );

                if (!response.ok) {
                    setPatientRemoteMatches([]);
                    return;
                }

                const result = await response.json();
                const data = Array.isArray(result?.data) ? result.data : [];
                setPatientRemoteMatches(data);
            } catch (_) {
                setPatientRemoteMatches([]);
            }
        };

        const timer = setTimeout(runPatientSearch, 300);
        return () => clearTimeout(timer);
    }, [patientAutocompleteValue, token, patients]);

    useEffect(() => {
        if (addNewPatient || searchPatientMode) {
            setPatientAutocompleteValue('');
            setPatientRemoteMatches([]);
            return;
        }

        if (!formData?.patient) {
            setPatientAutocompleteValue('');
            setPatientRemoteMatches([]);
            return;
        }

        const selectedPatientOption = (patients || []).find((patient) => Number(patient.id) === Number(formData?.patient));
        setPatientAutocompleteValue(selectedPatientOption ? getPatientOptionLabel(selectedPatientOption) : '');
    }, [formData?.patient, patients, addNewPatient, searchPatientMode]);
    return (
        <>
            <Grid item xs={12}>
                <FormHeading>Patient Details</FormHeading>
            </Grid>
            <Grid container spacing={0} sx={{ marginTop: '8px' }}>
                {!searchPatientMode && (patientDetails.email || addNewPatient || formData.email) && (
                    addNewPatient ? (
                        // Typeahead Email field for Add New Patient flow (moved above name)
                        <>
                            <FormInputLabel label={'Email'} labelSize={6} required={true} />
                            <Grid item xs={6} sx={{ marginBottom: '8px', width: '100%', maxWidth: '100%' }}>
                                <div style={{ position: 'relative' }} ref={emailDropdownRef}>
                                    <TextField
                                        fullWidth
                                        name="email"
                                        value={formData?.email || ''}
                                        onChange={handleEmailInputChange}
                                        onBlur={handleEmailBlur}
                                        placeholder="Enter Patient's Email"
                                        required={true}
                                        error={!!emailError}
                                        helperText={emailError}
                                        sx={{
                                            '& .MuiOutlinedInput-input::placeholder': {
                                                color: '#666',
                                                opacity: 1,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                border: '1px solid #CBC1B7',
                                                transition: 'all 0.3s ease',
                                                backgroundColor: '#fff',
                                                '&:hover': {
                                                    border: '1px solid #999',
                                                },
                                                '&.Mui-focused': {
                                                    border: '1px solid #ff5f39',
                                                    boxShadow: '0 0 0 2px rgba(255, 95, 57, 0.1)',
                                                },
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                                color: '#333',
                                                padding: '12px 16px',
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: 'red',
                                            },
                                        }}
                                    />
                                    {showEmailDropdown && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                [emailDropdownPosition === 'above' ? 'bottom' : 'top']: '100%',
                                                left: 0,
                                                right: 0,
                                                zIndex: 1000,
                                                maxHeight: '180px',
                                                overflow: 'auto',
                                                marginTop: emailDropdownPosition === 'below' ? '8px' : 0,
                                                marginBottom: emailDropdownPosition === 'above' ? '8px' : 0,
                                                border: '1px solid #CBC1B7',
                                                maxWidth: '100%',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '0px',
                                                boxShadow: 'none'
                                            }}
                                        >
                                            <div style={{ padding: 0 }}>
                                                {emailMatches.map((p, index) => (
                                                    <div
                                                        key={p.id}
                                                        onClick={() => handleEmailPatientSelect(p)}
                                                        style={{
                                                            padding: '8px 16px',
                                                            minHeight: '32px',
                                                            backgroundColor: '#f5f5f5',
                                                            borderBottom: index < emailMatches.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                            cursor: 'pointer',
                                                            borderRadius: '0px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e8e8e8'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                    >
                                                        <div style={{
                                                            fontSize: '13px',
                                                            fontWeight: 500,
                                                            color: '#333',
                                                            lineHeight: '1.2'
                                                        }}>
                                                            {p.email}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '11px',
                                                            color: '#666',
                                                            lineHeight: '1.2',
                                                            marginTop: '2px'
                                                        }}>
                                                            {`${p.first_name || ''} ${p.last_name || ''}`.trim()}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Grid>
                        </>
                    ) : (
                        <FormTextInput
                            disabled={true}
                            handleChange={handleChange}
                            label="Email"
                            name="email"
                            placeholder={''}
                            required={true}
                            value={patientDetails.email || formData.email}
                            style={{ marginBottom: '8px' }}
                            error={errors.email}
                            id="standard-error-helper-text"
                        />
                    )
                )}

                {!addNewPatient && !formData?.name && !searchPatientMode ? (
                    <FormSearhcAndSelectInput
                        label="Patient"
                        name="patient"
                        required={true}
                        placeholder="Select or search a patient"
                        options={patientDropdownOptions}
                        value={patientAutocompleteValue}
                        onChange={handlePatientAutocompleteChange}
                        renderOption={(props, option) => (
                            <li {...props}>
                                {option === '+ Add new patient' ? (
                                    <span style={{ fontStyle: 'italic' }}>{option}</span>
                                ) : (
                                    option
                                )}
                            </li>
                        )}
                        style={{ marginBottom: '8px' }}
                        disabled={formData?.form_status === 'edit' || isPastAppointment() || isAppointmentCancelled()}
                    />
                ) : (!searchPatientMode && (
                    // Show patient name input only when adding a new patient AND we've decided to reveal more fields
                    addNewPatient ? (
                        showNewPatientDetails && (
                            <FormTextInput
                                handleChange={handleChange}
                                label="Full Name"
                                name="name"
                                placeholder="Enter Patient's Name"
                                required={true}
                                value={formData?.name}
                                style={{ marginBottom: '8px' }}
                                error={errors.name}
                                id="standard-error-helper-text"
                                disabled={formData?.patient ? true : false}
                            />
                        )
                    ) : (
                        <FormTextInput
                            handleChange={handleChange}
                            label="Patient"
                            name="name"
                            placeholder="Enter Patient name"
                            required={true}
                            value={toTitleCase(formData?.name || '')}
                            style={{ marginBottom: '8px' }}
                            error={errors.name}
                            id="standard-error-helper-text"
                            disabled={formData?.form_status === 'edit' || (formData?.name && !addNewPatient) ? true : false}
                        />
                    )
                ))}

                {/* Search Patient Mode */}
                {searchPatientMode && (
                    <>
                        <FormInputLabel label={'Search Patient'} labelSize={6} />
                        <Grid item xs={6} sx={{ marginBottom: '8px', width: '100%', maxWidth: '100%' }}>
                            <div style={{ position: 'relative' }} ref={searchDropdownRef}>
                                <TextField
                                    fullWidth
                                    name="searchPatient"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by name, email"
                                    required={false}
                                    sx={{
                                        '& .MuiOutlinedInput-input::placeholder': {
                                            color: '#666',
                                            opacity: 1,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            border: '1px solid #CBC1B7',
                                            transition: 'all 0.3s ease',
                                            backgroundColor: '#fff',
                                            '&:hover': {
                                                border: '1px solid #999',
                                            },
                                            '&.Mui-focused': {
                                                border: '1px solid #ff5f39',
                                                boxShadow: '0 0 0 2px rgba(255, 95, 57, 0.1)',
                                            },
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '14px',
                                            color: '#333',
                                            padding: '12px 16px',
                                        },
                                    }}
                                />
                                {showSearchDropdown && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            [searchDropdownPosition === 'above' ? 'bottom' : 'top']: '100%',
                                            left: 0,
                                            right: 0,
                                            zIndex: 1000,
                                            maxHeight: '220px',
                                            overflow: 'auto',
                                            marginTop: searchDropdownPosition === 'below' ? '8px' : 0,
                                            marginBottom: searchDropdownPosition === 'above' ? '8px' : 0,
                                            border: '1px solid #CBC1B7',
                                            maxWidth: '100%',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '0px',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        <div style={{ padding: 0 }}>
                                            {searchResults.map((p, index) => (
                                                <div
                                                    key={p.id}
                                                    onClick={() => handleSearchPatientSelect(p)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        minHeight: '32px',
                                                        backgroundColor: '#f5f5f5',
                                                        borderBottom: index < searchResults.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                        cursor: 'pointer',
                                                        borderRadius: '0px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e8e8e8'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                >
                                                    <div style={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: '#333',
                                                        lineHeight: '1.2'
                                                    }}>
                                                        {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: '#666',
                                                        lineHeight: '1.2',
                                                        marginTop: '2px'
                                                    }}>
                                                        {p.email || p.contact_number || ''}
                                                    </div>
                                                </div>
                                            ))}
                                            {(!searchResults || searchResults.length === 0) && (
                                                <div style={{ padding: '10px 16px', fontSize: '12px', color: '#666' }}>
                                                    {isSearchLoading ? 'Searching...' : 'No matches found'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Grid>
                    </>
                )}

                {((addNewPatient ? showNewPatientDetails : (patientDetails.phone || formData.phone))) && (
                    <FormTextInput
                        formData={formData}
                        disabled={addNewPatient ? (formData?.patient ? true : false) : true}
                        handleChange={handlePhoneChange}
                        type="tel"
                        placeholder={addNewPatient ? "Enter Patient's Phone" : ""}
                        label="Phone Number"
                        name="phone"
                        required={true}
                        value={addNewPatient ? formatPhoneNumber(formData?.phone) : formatPhoneNumber(patientDetails.phone || (formData.phone ? `1${formData.phone}` : ''))}
                        maxLength="17" // +1 (123) 456-7890 is 17 characters
                        style={{ marginBottom: '8px' }}
                    />
                )}

                {((addNewPatient ? showNewPatientDetails : (patientDetails.dob || formData.dob))) && (
                    <FormTextInput
                        formData={formData}
                        disabled={addNewPatient ? (formData?.patient ? true : false) : true}
                        handleChange={handleChange}
                        type="date"
                        placeholder={addNewPatient ? 'Select DOB' : ''}
                        label="Date of Birth"
                        name="dob"
                        required={true}
                        value={addNewPatient ? formData?.dob : (patientDetails.dob || formData.dob)}
                        endDate={new Date()}
                        invoice={true}
                        style={{ marginBottom: '8px' }}
                    />
                )}

                {((addNewPatient ? showNewPatientDetails : (patientDetails.gender || formData.gender))) &&
                    (addNewPatient ? (
                        <FormSelectInput
                            formData={formData}
                            label={'Gender'}
                            name={'gender'}
                            handleChange={handleChange}
                            placeholder={'Select a Gender'}
                            required={true}
                            options={genderOptions}
                            optionKey={'id'}
                            optionValue={['value']}
                            value={formData.gender}
                            style={{ marginBottom: '8px' }}
                            disabled={formData?.patient ? true : false}

                        />
                    ) : (
                        <FormTextInput
                            formData={formData}
                            disabled={true}
                            handleChange={handleChange}
                            label={'Gender'}
                            name={'gender'}
                            required={true}
                            value={toTitleCase(patientDetails.gender || formData.gender || '')}
                            style={{ marginBottom: '8px' }}
                        />
                    ))}
            </Grid>

            <Grid item xs={12} sx={{ marginTop: '32px' }}>
                <FormHeading>Appointment Details</FormHeading>
            </Grid>

            <Grid container spacing={0} sx={{ marginTop: '8px' }}>
                <FormSelectInput
                    formData={formData}
                    label="Provider"
                    name="provider"
                    handleChange={handleChange}
                    placeholder="Select a Provider"
                    required={true}
                    options={
                        business?.id
                            ? [
                                { id: `business_${business.id}`, first_name: business?.name, last_name: '(Business)' },
                                ...providers,
                              ]
                            : providers
                    }
                    optionKey={'id'}
                    optionValue={['first_name', 'last_name']}
                    value={formData.provider}
                    style={{ marginBottom: '8px' }}
                    disabled={isPastAppointment() || isAppointmentCancelled()}

                />
                {formData?.appointmentStatus && (
                    <FormTextInput
                        formData={formData}
                        label="Appointment Status"
                        name="appointmentStatus"
                        // handleChange={handleChange}
                        placeholder="Appointment Status"
                        type="text"
                        disabled={true}
                        value={toTitleCase(formData.appointmentStatus)}
                        style={{ marginBottom: '8px' }}
                    />
                )}
                {formData?.paymentStatus && (
                    <FormTextInput
                        formData={formData}
                        label="Payment Status"
                        name="paymentStatus"
                        // handleChange={handleChange}
                        placeholder="Payment Status"
                        type="text"
                        disabled={true}
                        value={toTitleCase(formData.paymentStatus)}
                        style={{ marginBottom: '8px' }}
                    />
                )}
                <Box id="reschedule-section" sx={{ display: 'contents' }}>
                    <Box id="reschedule-date" sx={{ display: 'contents' }}>
                        <FormTextInput
                            formData={formData}
                            label="Date of Service"
                            name="dateOfService"
                            handleChange={handleChange}
                            placeholder="Select a Date"
                            required={true}
                            type="date"
                            value={formData.dateOfService}
                            disabled={isPastAppointment() || isTimeDisabled || isAppointmentCancelled()}
                            startDate={new Date()}
                            style={{ marginBottom: '8px' }}
                            InputProps={{
                                endAdornment: <CalendarToday style={{ color: '#757575' }} />,
                            }}
                            invoice={false}
                        />
                    </Box>

                    <Box id="reschedule-time" sx={{ display: 'contents' }}>
                        <FormTextInput
                            formData={formData}
                            label="Time of Service"
                            name="timeOfService"
                            handleChange={handleChange}
                            placeholder="Select a Time"
                            required={true}
                            type="time"
                            value={formData.timeOfService}
                            disabled={isPastAppointment() || isTimeDisabled || isAppointmentCancelled()}
                            readOnly={true}
                            min={getMinTime(formData.dateOfService)}
                            style={{ marginBottom: '8px' }}
                            invoice={false}
                            bookedSlots={bookedSlots}
                            providerAvailabilityData={providerAvailabilityData}
                            oooData={oooData}
                            slotsGap={
                                isBusinessSelected
                                    ? 15
                                    : (formData?.appointment_type === 'telemedicine'
                                        ? Number(formData?.providerDetail?.telemedicine_consultation_duration) || 30
                                        : Number(formData?.providerDetail?.in_person_consultation_duration) || 30)
                            }
                        />
                    </Box>
                </Box>

                <FormSelectInput
                    formData={formData}
                    label="Duration"
                    name="duration"
                    handleChange={handleChange}
                    placeholder="Duration of Appointment"
                    required={formData?.form_status === 'add'}
                    options={serviceTimeOptions}
                    optionKey={'id'}
                    optionValue={['service']}
                    value={
                        formData?.duration !== undefined && formData?.duration !== null && formData?.duration !== ''
                            ? Number(formData.duration)
                            : (
                                isBusinessSelected
                                    ? 15
                                    : (
                                        formData?.appointment_type === 'telemedicine'
                                            ? Number(formData?.providerDetail?.telemedicine_consultation_duration)
                                            : Number(formData?.providerDetail?.in_person_consultation_duration)
                                    )
                              )
                    }
                    style={{ marginBottom: '8px' }}
                    disabled={isPastAppointment() || isAppointmentCancelled()}
                />

                {(() => {
                    // Check if provider has telemedicine enabled
                    const hasTelemedicine = formData?.providerDetail?.telemedicine_consultation_duration && 
                                           formData?.providerDetail?.telemedicine_consultation_price;
                    
                    // Only add telemedicine option if provider has it configured
                    const locationOptions = [
                        ...(locations || []), 
                        ...(hasTelemedicine ? [{ id: 'telemedicine', location: 'Telemedicine' }] : [])
                    ];
                    
                    const handleLocationSelectChange = (e) => {
                        const { value } = e.target;
                        if (value === 'telemedicine') {
                            setFormData({
                                ...formData,
                                location_id: 'telemedicine',
                                appointment_type: 'telemedicine',
                            });
                        } else {
                            setFormData({
                                ...formData,
                                location_id: value ? Number(value) : '',
                                appointment_type: 'in_office',
                            });
                        }
                    };
                    return (
                        <FormSelectInput
                            formData={formData}
                            label="Location"
                            name="location_id"
                            handleChange={handleLocationSelectChange}
                            placeholder="Select a Location"
                            required={formData?.form_status === 'add'}
                            options={locationOptions}
                            optionKey={'id'}
                            optionValue={['location']}
                            value={
                                formData.location_id === 'telemedicine'
                                    ? 'telemedicine'
                                    : (formData.location_id ? Number(formData.location_id) : '')
                            }
                            style={{ marginBottom: '8px' }}
                            disabled={isPastAppointment() || isAppointmentCancelled()}
                        />
                    );
                })()}
            </Grid>

            <Grid item xs={12} sx={{ marginTop: '32px' }}>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: theme.colorTableHeaderBackground,
                        paddingBottom: '10px',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins'
                        }}
                    >
                        Service Details
                    </Box>
                    {(() => {
                        const isPending = String(formData?.appointmentStatus || '').toLowerCase().includes('pending');
                        const canAddServices = (formData?.form_status === 'add' || isPending) && handleAddAnotherService;
                        return canAddServices && (
                            <Box
                                className={'primary-link cursor-pointer'}
                                onClick={handleAddAnotherService}
                            >
                                + Add Another Service
                            </Box>
                        );
                    })()}
                </Box>
            </Grid>

            {[...Array(servicesCount)].reverse().map((_, reverseIndex) => {
                const index = servicesCount - 1 - reverseIndex; // Convert reverse index back to original index
                const serviceNumber = servicesCount - reverseIndex; // Service number for display (newest is highest number)
                const showContainer = servicesCount > 1;
                return (
                    <Box 
                        key={index}
                        style={{ 
                            border: showContainer ? '1px solid #E1E1E1' : 'none',
                            borderRadius: showContainer ? '8px' : '0px',
                            padding: showContainer ? '16px' : '0px',
                            paddingLeft: showContainer ? '16px' : '0px',
                            paddingRight: showContainer ? '16px' : '0px',
                            marginBottom: '8px',
                            marginTop: index === servicesCount - 1 ? '12px' : '0px',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        <Grid container spacing={0} sx={{ marginTop: '0px' }}>
                            {isCreatedByPatient ?
                                <FormTextInput
                                    formData={formData}
                                    label={servicesCount > 1 ? `Service ${serviceNumber}` : 'Service'}
                                    name="service"
                                    required={formData?.form_status === 'add'}
                                    value={formData?.servicesData?.[index]?.serviceName || "Consult"}
                                    style={{ marginBottom: '8px' }}
                                    disabled={isCreatedByPatient}

                                /> :
                                <FormSearhcAndSelectInput
                                    label={servicesCount > 1 ? `Service ${serviceNumber}` : 'Service'}
                                    name="service"
                                    required={formData?.form_status === 'add'}
                                    placeholder="Select or search a service"
                                    options={
                                        [
                                            '+ Add new service',
                                            ...(services || [])
                                                .filter((s) => s.id !== 'add_new_service')
                                                .map((s) => s.name),
                                        ]
                                    }
                                    value={formData?.servicesData?.[index]?.serviceName ?? ''}
                                    onChange={(e, newValue) => {
                                        if (newValue === '+ Add new service') {
                                            handleServiceChange({ target: { name: 'service', value: 'add_new_service' } }, index);
                                            return;
                                        }
                                        const svc = (services || []).find((s) => s.id !== 'add_new_service' && s.name === newValue);
                                        if (svc) {
                                            handleServiceChange({ target: { name: 'service', value: svc.id } }, index);
                                        } else {
                                            handleServiceChange({ target: { name: 'service', value: newValue ?? '' } }, index);
                                        }
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            {option === '+ Add new service' ? (
                                                <span style={{ fontStyle: 'italic' }}>{option}</span>
                                            ) : (
                                                option
                                            )}
                                        </li>
                                    )}
                                    style={{ marginBottom: '8px' }}
                                    disabled={isPastAppointment() || isAppointmentCancelled()}
                                />
                            }

                            <FormTextInput
                                formData={formData}
                                label="Description"
                                name="description"
                                handleChange={(e) => handleServiceChange(e, index)}
                                placeholder="Enter a Description"
                                required={formData?.form_status === 'add'}
                                value={formData?.servicesData?.[index]?.description}
                                style={{ marginBottom: '8px' }}
                                disabled={isPastAppointment() || isAppointmentCancelled()}
                            />

                            <FormTextInput
                                formData={formData}
                                disabled={isPastAppointment() || isAppointmentCancelled()}
                                label="Price"
                                name="price"
                                handleChange={(e) => handlePriceChange(e, index)}
                                placeholder="0.00"
                                required={formData?.form_status === 'add'}
                                value={
                                    formData?.servicesData?.[index]?.price !== undefined 
                                        ? formatPriceWithCents(formData?.servicesData?.[index]?.price)
                                        : (isCreatedByPatient
                                            ? formatPriceWithCents(formData?.appointment_type === 'telemedicine'
                                                ? formData?.providerDetail?.telemedicine_consultation_price
                                                : formData?.providerDetail?.in_person_consultation_price)
                                            : '')
                                }  
                                style={{ marginBottom: '0px' }}
                                type="number"
                                inputProps={{
                                    step: "0.01",
                                    min: "0",
                                    inputMode: "decimal",
                                    style: { textAlign: 'right' },
                                    onFocus: (e) => handlePriceFocus(e, index),
                                    onKeyDown: (ev) => {
                                        const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                                        if (allowed.includes(ev.key)) return;
                                        const isDigit = /[0-9]/.test(ev.key);
                                        if (!isDigit) {
                                            ev.preventDefault();
                                            return;
                                        }
                                    },
                                    onPaste: (ev) => {
                                        const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                        if (!/^\d*$/.test(paste)) {
                                            ev.preventDefault();
                                        }
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <Box
                                            sx={{
                                                backgroundColor: 'rgba(203, 193, 183, 1)',
                                                color: 'rgba(87, 83, 78, 1)',
                                                height: '42px',
                                                width: '42px !important',
                                                minWidth: '42px !important',
                                                maxWidth: '42px !important',
                                                flexBasis: '42px !important',
                                                flexGrow: '0 !important',
                                                flexShrink: '0 !important',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginLeft: '-14px',
                                                borderTopLeftRadius: '7px',
                                                borderBottomLeftRadius: '7px',
                                            }}
                                        >
                                            <AttachMoney fontSize="small" />
                                        </Box>
                                    ),
                                    sx: {
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield',
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                        '& input[type=number]::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    }
                                }}
                                additionAction={
                                    (() => {
                                        const isPending = String(formData?.appointmentStatus || '').toLowerCase().includes('pending');
                                        const canManageServices = servicesCount > 1 && (formData?.form_status === 'add' || isPending);
                                        return canManageServices && (
                                            <Box
                                                className={'danger-link'}
                                                sx={{ cursor: 'pointer', textAlign: 'right', marginTop: '12px', marginBottom: '0px' }}
                                                onClick={() => {
                                                    handleRemoveService(index);
                                                }}
                                            >
                                                - Remove Service
                                            </Box>
                                        );
                                    })()
                                }
                            />
                        </Grid>
                    </Box>
                );
            })}

            {/* Total Row */}
            <Grid item xs={12} style={{ marginTop: '16px' }}>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: theme.colorTableHeaderBackground,
                        paddingBottom: '10px',
                        marginBottom: '12px',
                        width: '100%'
                    }}
                />
            </Grid>
            <Grid container spacing={0} sx={{ marginTop: '0px' }}>
                <Grid item xs={6} sx={{ paddingLeft: '0px', paddingRight: '0px' }}>
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        Total
                    </Box>
                </Grid>
                <Grid item xs={6} sx={{ paddingLeft: '0px', paddingRight: '0px' }}>
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins',
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}
                    >
                        ${(() => {
                            const cleanPrice = (price) => {
                                if (typeof price === 'string') {
                                    return price.replace(/,/g, '');
                                }
                                return price;
                            };
                            const total = (formData?.servicesData || [])
                                .reduce((sum, service) => {
                                    const cleanedPrice = cleanPrice(service?.price);
                                    const numPrice = parseFloat(cleanedPrice) || 0;
                                    return sum + numPrice;
                                }, 0);
                            return total.toFixed(2);
                        })()}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}

